import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Award, FileText, Building2, Loader2 } from 'lucide-react';
import Countdown from '@/components/common/Countdown';
import { useConference } from '@/context/ConferenceContext';
import { getMembersByUser } from '@/services/api';
import { toast } from 'sonner';

const Home = () => {
  const { shortName, loading: confLoading, importantDetails, siteConfig, loginDetails, getConferenceVenue } = useConference();
  const [speakers, setSpeakers] = useState(() => {
    // Initialize with cached data for instant display
    try {
      const cached = localStorage.getItem('conferenceSpeakers');
      if (cached) {
        const allMembers = JSON.parse(cached);
        return allMembers.filter(m => m.category?.toLowerCase() !== 'ocm').slice(0, 8);
      }
      return [];
    } catch {
      return [];
    }
  });
  const [committeeMembers, setCommitteeMembers] = useState(() => {
    // Initialize with cached OCM data
    try {
      const cached = localStorage.getItem('conferenceSpeakers');
      if (cached) {
        const allMembers = JSON.parse(cached);
        return allMembers.filter(m => m.category?.toLowerCase() === 'ocm').slice(0, 8);
      }
      return [];
    } catch {
      return [];
    }
  });
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false for immediate loading

  useEffect(() => {
    const loadSpeakers = async (username) => {
      setLoading(true);
      try {
        // Fetch all members using the same API
        const allMembers = await getMembersByUser(username);
        
        // Filter speakers (exclude OCM)
        const speakersData = (allMembers || []).filter(member => member.category?.toLowerCase() !== 'ocm');
        setSpeakers(speakersData.slice(0, 8));
        
        // Filter OCM members
        const ocmData = (allMembers || []).filter(member => member.category?.toLowerCase() === 'ocm');
        setCommitteeMembers(ocmData.slice(0, 8));
        
        // Keep sponsors as empty array since API doesn't exist
        setSponsors([]);
      } catch (error) {
        console.error('Failed to fetch home page data:', error);
        // Keep cached speakers on error
      } finally {
        setLoading(false);
      }
    };

    const fetchData = async () => {
      // Try to get username from multiple sources
      const username = loginDetails?.username || localStorage.getItem('conferenceUsername');
      
      if (!username) {
        // Try to get from cached login details
        try {
          const cachedLogin = localStorage.getItem('conferenceLoginDetails');
          if (cachedLogin) {
            const parsed = JSON.parse(cachedLogin);
            if (parsed?.username) {
              await loadSpeakers(parsed.username);
              return;
            }
          }
        } catch (e) {
          console.error('Failed to get cached username:', e);
        }
        setLoading(false);
        return;
      }

      await loadSpeakers(username);
    };

    fetchData();
  }, [loginDetails?.username]);

  const featuredSpeakers = speakers; // Already sliced to 4 in state initialization
  const platinumSponsors = sponsors.filter((s) => s.sponsorType === 'platinum' || s.sponsorType === 'Platinum');

  // Parse conference dates from API string (e.g., "September 12-14, 2026" or "September 12, 2026")
  const parseConferenceDates = (dateString) => {
    if (!dateString) return ['September 17, 2026', 'September 18, 2026', 'September 19, 2026'];
    
    try {
      // Handle both range format (12-14) and single date format (12)
      const rangeMatch = dateString.match(/^(\w+)\s+(\d+)-(\d+),\s+(\d+)$/);
      const singleMatch = dateString.match(/^(\w+)\s+(\d+),\s+(\d+)$/);
      
      let month, startDay, endDay, year;
      
      if (rangeMatch) {
        // Range format: "September 12-14, 2026"
        [, month, startDay, endDay, year] = rangeMatch;
        startDay = parseInt(startDay);
        endDay = parseInt(endDay);
      } else if (singleMatch) {
        // Single date format: "September 12, 2026"
        [, month, startDay, year] = singleMatch;
        startDay = parseInt(startDay);
        endDay = startDay; // Same day for single date
      } else {
        return ['September 17, 2026', 'September 18, 2026', 'September 19, 2026'];
      }
      
      // Generate dates for the range
      const dates = [];
      for (let day = startDay; day <= endDay; day++) {
        dates.push(`${month} ${day}, ${year}`);
      }
      
      return dates;
    } catch (error) {
      console.error('Error parsing conference dates:', error);
      return ['September 17, 2026', 'September 18, 2026', 'September 19, 2026'];
    }
  };

  const conferenceDates = parseConferenceDates(importantDetails?.ConferenceDates);

  // Don't show loading screen, render immediately with cached data
  if ((loading || confLoading) && speakers.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-sky-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading conference information...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {importantDetails?.ConferenceTitle ? 
            importantDetails.ConferenceTitle.replace(/<[^>]*>/g, '') : ""} - Home
        </title>
        <meta name="description" content={siteConfig?.description || 'Join leading civil engineers, researchers, and industry professionals for three days of innovation, collaboration, and knowledge sharing.'} />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-32" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              <span className="text-gradient">
                {importantDetails?.ConferenceTitle ? 
                  <span dangerouslySetInnerHTML={{ __html: importantDetails.ConferenceTitle }} /> : 
                  'CCAI2026'
                }
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-700 mb-4 max-w-3xl mx-auto">
              {siteConfig?.tagline || 'Building the Future of Infrastructure'}
            </p>
            <p className="text-base text-gray-600 mb-8 max-w-2xl mx-auto">
              {siteConfig?.description || 'Join leading civil engineers, researchers, and industry professionals for three days of innovation, collaboration, and knowledge sharing.'}
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <Link
                to="/register"
                data-testid="hero-register-btn"
                className="btn-primary inline-flex items-center gap-2"
              >
                <Users size={20} />
                Register Now
              </Link>
              <Link
                to="/submit"
                data-testid="hero-submit-btn"
                className="btn-secondary inline-flex items-center gap-2"
              >
                <FileText size={20} />
                Submit Abstract
              </Link>
            </div>

            {/* Key Info */}
            <div className="flex flex-wrap justify-center gap-6 text-gray-700 mb-12">
              <div className="flex items-center gap-2">
                <Calendar size={20} className="text-sky-500" />
                <span className="font-semibold">
                  {importantDetails?.ConferenceDates || ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={20} className="text-sky-500" />
                <span className="font-semibold">
                  {getConferenceVenue()}
                </span>
              </div>
            </div>

            {/* Countdown */}
            <Countdown />
          </div>
        </div>
      </section>

      {/* Organizing Committee Section */}
      <section className="section-spacing bg-gray-50" data-testid="committee-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Organizing Committee</h2>
            <p className="text-lg text-gray-600">Meet the dedicated team organizing this conference</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {committeeMembers.map((member) => (
              <div
                key={member.id}
                className="card-hover bg-white rounded-2xl overflow-hidden shadow-sm hover:-translate-y-1 transition-transform p-6 border-2 border-gray-100"
                data-testid={`committee-card-${member.id}`}
              >
                <div className="text-center">
                  {member.photo ? (
                    <img 
                      src={member.photo} 
                      alt={member.name} 
                      className="w-32 h-32 rounded-full object-cover mx-auto mb-4 shadow-md"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-md">
                      {member.name?.charAt(0) || '?'}
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{member.affiliation}</p>
                  {member.biography && (
                    <p className="text-base text-blue-600 font-extrabold mb-3 line-clamp-2">{member.biography}</p>
                  )}
                  <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors border-transparent bg-green-100 text-green-800">
                    Committee Member
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/committee" data-testid="view-all-committee-btn" className="btn-secondary">
              View All Committee Members
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Speakers */}
      <section className="section-spacing bg-white" data-testid="speakers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Featured Speakers</h2>
            <p className="text-lg text-gray-600">Learn from industry leaders and researchers</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredSpeakers.map((speaker) => (
              <div
                key={speaker.id}
                className="card-hover bg-white rounded-2xl overflow-hidden shadow-sm hover:-translate-y-1 transition-transform p-6 border-2 border-gray-100"
                data-testid={`speaker-card-${speaker.id}`}
              >
                <div className="text-center">
                  {speaker.photo ? (
                    <img 
                      src={speaker.photo} 
                      alt={speaker.name} 
                      className="w-32 h-32 rounded-full object-cover mx-auto mb-4 shadow-md"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-gradient-to-br from-sky-600 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-md">
                      {speaker.name?.charAt(0) || '?'}
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{speaker.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{speaker.affiliation}</p>
                  {speaker.biography && (
                    <p className="text-base text-blue-600 font-extrabold mb-3 line-clamp-2">{speaker.biography}</p>
                  )}
                  <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors border-transparent bg-sky-100 text-sky-800">
                    {speaker.category ? speaker.category.toUpperCase() : 'Featured Speaker'}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/speakers" data-testid="view-all-speakers-btn" className="btn-secondary">
              View All Speakers
            </Link>
          </div>
        </div>
      </section>

      {/* Conference Tracks */}
      <section className="section-spacing bg-gray-50" data-testid="tracks-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Conference Tracks</h2>
            <p className="text-lg text-gray-600">Explore cutting-edge topics and research areas</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Priority Topics */}
            <div className="card-hover p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border-2 border-blue-200 shadow-md">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-blue-500">
                <Building2 size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Civil Engineering</h3>
              <p className="text-gray-700 leading-relaxed">
                Structural analysis, foundation design, soil mechanics, and innovative construction techniques for modern infrastructure development.
              </p>
            </div>

            <div className="card-hover p-8 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border-2 border-green-200 shadow-md">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-green-500">
                <Building2 size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Infrastructure</h3>
              <p className="text-gray-700 leading-relaxed">
                Urban planning, smart cities, transportation systems, sustainable infrastructure, and resilient design for future communities.
              </p>
            </div>

            <div className="card-hover p-8 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border-2 border-purple-200 shadow-md">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-purple-500">
                <Building2 size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Architecture</h3>
              <p className="text-gray-700 leading-relaxed">
                Architectural design, building information modeling (BIM), sustainable architecture, and integration with civil engineering.
              </p>
            </div>

            {/* Additional Topics from Config */}
            {(siteConfig?.tracks || [
              { id: 'structural', name: 'Structural Engineering', color: '#0ea5e9' },
              { id: 'geotechnical', name: 'Geotechnical Engineering', color: '#8b5cf6' },
              { id: 'transportation', name: 'Transportation Engineering', color: '#f59e0b' },
              { id: 'coastal', name: 'Coastal Engineering', color: '#ec4899' },
              { id: 'environmental', name: 'Environmental Engineering', color: '#10b981' },
              { id: 'water-resources', name: 'Water Resources Engineering', color: '#06b6d4' },
            ]).slice(0, 6).map((track, index) => (
              <div
                key={track.id}
                className="card-hover p-8 bg-white rounded-2xl border-2 border-gray-200 shadow-sm hover:shadow-lg transition-shadow"
                data-testid={`track-card-${track.id}`}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${track.color}20` }}
                >
                  <Building2 size={32} style={{ color: track.color }} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{track.name}</h3>
                <p className="text-gray-600 leading-relaxed">
                  Advanced research and practical applications in {track.name.toLowerCase()}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/call-for-papers" className="btn-primary inline-flex items-center gap-2">
              <FileText size={20} />
              Submit Your Research
            </Link>
          </div>
        </div>
      </section>

     

      {/* Schedule Preview */}
      <section className="section-spacing bg-white" data-testid="schedule-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Conference Schedule</h2>
            <p className="text-lg text-gray-600">Three days of knowledge sharing and networking</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {conferenceDates.map((date, index) => {
              const dayNumber = index + 1;
              const dayColors = [
                { bg: 'bg-sky-100', text: 'text-sky-500' },
                { bg: 'bg-green-100', text: 'text-green-500' },
                { bg: 'bg-purple-100', text: 'text-purple-500' },
                { bg: 'bg-orange-100', text: 'text-orange-500' },
                { bg: 'bg-pink-100', text: 'text-pink-500' },
              ];
              const colorScheme = dayColors[index % dayColors.length];
              
              return (
                <div
                  key={dayNumber}
                  className="card-hover p-6 bg-white rounded-2xl border-2 border-gray-100 shadow-sm hover:shadow-lg transition-shadow"
                  data-testid={`schedule-day-${dayNumber}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 ${colorScheme.bg} rounded-xl flex items-center justify-center`}>
                      <Calendar size={24} className={colorScheme.text} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">Day {dayNumber}</h3>
                      <p className="text-sm text-gray-600">{date}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {dayNumber === 1 && (
                      <>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">9:00 AM - Opening Ceremony</div>
                          <div className="text-gray-600">Welcome & Keynote Address</div>
                        </div>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">10:30 AM - Technical Sessions</div>
                          <div className="text-gray-600">Structural Engineering Track</div>
                        </div>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">2:00 PM - Workshops</div>
                          <div className="text-gray-600">BIM & Digital Design</div>
                        </div>
                      </>
                    )}
                    {dayNumber === 2 && (
                      <>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">9:00 AM - Keynote Sessions</div>
                          <div className="text-gray-600">Industry Leaders Panel</div>
                        </div>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">11:00 AM - Parallel Tracks</div>
                          <div className="text-gray-600">Geotechnical & Transportation</div>
                        </div>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">3:00 PM - Poster Presentations</div>
                          <div className="text-gray-600">Student Research Showcase</div>
                        </div>
                      </>
                    )}
                    {dayNumber === 3 && (
                      <>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">9:00 AM - Technical Workshops</div>
                          <div className="text-gray-600">Sustainable Infrastructure</div>
                        </div>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">1:00 PM - Closing Ceremony</div>
                          <div className="text-gray-600">Awards & Future Directions</div>
                        </div>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">4:00 PM - Networking</div>
                          <div className="text-gray-600">Cocktail Reception</div>
                        </div>
                      </>
                    )}
                    {dayNumber > 3 && (
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">Full Day Sessions</div>
                        <div className="text-gray-600">Technical presentations and workshops</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Link to="/schedule" data-testid="view-full-schedule-btn" className="btn-secondary">
              View Full Schedule
            </Link>
          </div>
        </div>
      </section>

      {/* Sponsors */}
      <section className="section-spacing bg-gray-50" data-testid="sponsors-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Sponsors</h2>
            <p className="text-lg text-gray-600">Supported by industry leaders</p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-8">
            {platinumSponsors.map((sponsor) => (
              <a
                key={sponsor.sponsorId}
                href={sponsor.website}
                target="_blank"
                rel="noopener noreferrer"
                className="card-hover"
                data-testid={`sponsor-${sponsor.sponsorId}`}
              >
                <img
                  src={sponsor.logo}
                  alt={sponsor.name}
                  className="h-20 object-contain grayscale hover:grayscale-0 transition-all"
                />
              </a>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/sponsors" data-testid="become-sponsor-btn" className="btn-primary">
              Become a Sponsor
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-gradient-to-r from-sky-500 to-sky-600" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Join Us at CCAI-2026
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Don't miss this opportunity to connect with leading civil engineers and researchers from around the world.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/register"
                data-testid="cta-register-btn"
                className="px-8 py-3 bg-white text-sky-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
              >
                Register Now
              </Link>
              <Link
                to="/contact"
                data-testid="cta-contact-btn"
                className="px-8 py-3 bg-sky-700 text-white rounded-xl font-semibold hover:bg-sky-800 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
